import React, { useEffect, useRef } from 'react';
import styles from './Common.module.css';
import { trafficData } from './trafficData';

/**
 * High-Fidelity Procedural Loading Screen
 * Replicates the Genova Dashboard Glow effect with mathematical precision.
 */
const TraffixLoadingScreen = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    let width, height;
    let globalTime = 0;
    let dataFrame = 0;
    const framesToInterpolate = 320; // 4 volte più lento

    const config = {
        radius: 0,
        baseColor: 'rgba(161, 227, 64, 1)',
        layers: 18,
        globalSpeed: 0.007,
        labelThreshold: 1.02,
    };

    const thetas = trafficData.thetas;
    const frames = trafficData.frames;

    function resize() {
        const dpr = window.devicePixelRatio || 1;
        width = window.innerWidth * dpr;
        height = window.innerHeight * dpr;
        canvas.width = width;
        canvas.height = height;
        canvas.style.width = window.innerWidth + 'px';
        canvas.style.height = window.innerHeight + 'px';
        config.radius = Math.min(width, height) * 0.28;
    }

    function constantDisturbance(angle, t) {
        return (
            Math.sin(angle * 4.0 + t * 1.2) * 4.0 +
            Math.sin(angle * 8.0 - t * 1.8) * 2.5 +
            Math.sin(angle * 25.0 + t * 2.5) * 1.2
        );
    }

    function layerNoise(angle, t, l) {
        const seed = l * 55.44;
        return (
            Math.sin(angle * 3.0 + t * 0.8 + seed) * 3.5 +
            Math.sin(angle * 12.0 - t * 0.5 + seed * 1.2) * 2.0
        );
    }

    function getPeakHeight(baseR) {
        const diff = Math.max(0, baseR - 1.0);
        const normalized = diff / (0.3 + diff);
        return Math.pow(normalized, 1.5) * config.radius * 0.9;
    }

    function getRadius(thetaIndex, layer, layerProgress, rStart, rEnd, alpha) {
        const baseR = rStart[thetaIndex] * (1 - alpha) + rEnd[thetaIndex] * alpha;
        const angle = thetas[thetaIndex];

        const peakHeight = getPeakHeight(baseR);
        const noiseBase = constantDisturbance(angle, globalTime);

        if (layer === -1) {
            return config.radius + peakHeight + noiseBase;
        } else {
            const turb = layerNoise(angle, globalTime, layer) * (0.3 + 0.7 * layerProgress);
            const peakInfluence = Math.pow(layerProgress, 1.6) * 1.1;
            const elevation = layer * 0.9;
            return config.radius + (peakHeight * peakInfluence) + noiseBase * (0.8 + 0.2 * layerProgress) + turb + elevation;
        }
    }

    function drawLabels(centerX, centerY, rStart, rEnd, alpha, anomaliesStart, anomaliesEnd) {

        function getTopUnique(anomalies) {
            let scoredAnomalies = anomalies.map(a => {
                let densityScore = 0;
                anomalies.forEach(other => {
                    if (a !== other) {
                        let dTheta = Math.abs(a.theta - other.theta);
                        if (dTheta > Math.PI) dTheta = 2 * Math.PI - dTheta;
                        if (dTheta < 0.4) {
                            densityScore += Math.max(0, other.radius - 1.0) * (1.0 - dTheta / 0.4);
                        }
                    }
                });
                return {
                    ...a,
                    totalScore: (Math.max(0, a.radius - 1.0)) + (densityScore * 0.8)
                };
            });

            let sorted = [...scoredAnomalies].sort((a, b) => b.totalScore - a.totalScore);
            let unique = [];
            let seen = new Set();
            for (let p of sorted) {
                let key = (p.street_name || '').toUpperCase() + '|' + (p.anomaly_type || '').toUpperCase();
                if (!seen.has(key)) {
                    seen.add(key);
                    unique.push(p);
                    if (unique.length >= 4) break;
                }
            }
            return unique;
        }

        let startTop = getTopUnique(anomaliesStart);
        let endTop = getTopUnique(anomaliesEnd);

        let allCandidates = [...startTop, ...endTop];
        let bestCandidatesMap = new Map();

        allCandidates.forEach(p => {
            let idx = thetas.findIndex(t => Math.abs(t - p.theta) < 0.001);
            if (idx === -1) return;
            let currentR = rStart[idx] * (1 - alpha) + rEnd[idx] * alpha;

            let key = (p.street_name || '').toUpperCase() + '|' + (p.anomaly_type || '').toUpperCase();

            if (!bestCandidatesMap.has(key) || currentR > bestCandidatesMap.get(key).currentR) {
                bestCandidatesMap.set(key, { ...p, currentR: currentR });
            }
        });

        // Convert map to array
        let rawCandidates = Array.from(bestCandidatesMap.values());

        // --- CONFLICT RESOLUTION BY STREET ---
        let streetGroups = new Map();
        rawCandidates.forEach(c => {
            let street = (c.street_name || '').toUpperCase();
            if (!streetGroups.has(street)) streetGroups.set(street, []);
            streetGroups.get(street).push(c);
        });

        let resolvedCandidates = [];
        streetGroups.forEach((group, street) => {
            if (street === 'NONE' || street === 'NAN' || street === '') {
                resolvedCandidates.push(...group);
                return;
            }

            // Definizione famiglie compatibili
            let setA = ['RALLENTAMENTO', 'AUMENTO VEICOLI'];
            let setB = ['SCORRIMENTO VELOCE', 'DIMINUZIONE VEICOLI'];

            let groupA = group.filter(c => setA.includes((c.anomaly_type || '').toUpperCase()));
            let groupB = group.filter(c => setB.includes((c.anomaly_type || '').toUpperCase()));

            if (groupA.length > 0 && groupB.length > 0) {
                // Conflitto rilevato. Trova il raggio massimo in ogni gruppo
                let maxRA = Math.max(...groupA.map(c => c.currentR));
                let maxRB = Math.max(...groupB.map(c => c.currentR));

                if (maxRA >= maxRB) {
                    resolvedCandidates.push(...groupA);
                } else {
                    resolvedCandidates.push(...groupB);
                }
            } else {
                resolvedCandidates.push(...group);
            }
        });

        // Keep top 5 globally after conflict resolution
        let finalCandidates = resolvedCandidates.sort((a, b) => b.currentR - a.currentR).slice(0, 5);

        let activeLabels = [];
        finalCandidates.forEach(p => {
            if (p.currentR > config.labelThreshold) {
                let peakH = getPeakHeight(p.currentR);
                let angle = p.theta;
                let baseDist = config.radius + constantDisturbance(angle, globalTime);
                let tipDist = baseDist + peakH;

                let startX = centerX + Math.cos(angle) * tipDist;
                let startY = centerY + Math.sin(angle) * tipDist;

                let opacity = Math.min(1, Math.max(0, (p.currentR - config.labelThreshold) / 0.05));

                activeLabels.push({
                    ...p,
                    angle,
                    startX,
                    startY,
                    targetY: startY + Math.sin(angle) * 15,
                    opacity
                });
            }
        });

        let leftLabels = activeLabels.filter(a => Math.cos(a.angle) <= 0).sort((a,b) => a.startY - b.startY);
        let rightLabels = activeLabels.filter(a => Math.cos(a.angle) > 0).sort((a,b) => a.startY - b.startY);

        function resolveCollisions(labels) {
            for(let i = 1; i < labels.length; i++) {
                if (labels[i].targetY - labels[i-1].targetY < 40) {
                    labels[i].targetY = labels[i-1].targetY + 40;
                }
            }
        }

        resolveCollisions(leftLabels);
        resolveCollisions(rightLabels);
        let finalLabels = [...leftLabels, ...rightLabels];

        finalLabels.forEach(p => {
            const isRight = Math.cos(p.angle) > 0;
            const targetX = p.startX + Math.cos(p.angle) * 25;
            const horizontalEnd = targetX + (isRight ? 60 : -60);

            ctx.beginPath();
            ctx.strokeStyle = `rgba(161, 227, 64, ${p.opacity * 0.6})`;
            ctx.lineWidth = 1;
            ctx.moveTo(p.startX, p.startY);
            ctx.lineTo(targetX, p.targetY);
            ctx.lineTo(horizontalEnd, p.targetY);
            ctx.stroke();

            ctx.fillStyle = `rgba(241, 245, 235, ${p.opacity})`;
            ctx.textAlign = isRight ? 'left' : 'right';

            let isNone = !p.street_name || p.street_name.toUpperCase() === "NONE" || p.street_name.toUpperCase() === "NAN";

            if (!isNone) {
                ctx.font = `bold ${11 * (window.devicePixelRatio || 1)}px 'Segoe UI', sans-serif`;
                ctx.fillText(p.street_name.toUpperCase(), horizontalEnd + (isRight ? 8 : -8), p.targetY - 5);

                ctx.font = `lighter ${12 * (window.devicePixelRatio || 1)}px 'Segoe UI', sans-serif`;
                ctx.fillText(p.anomaly_type, horizontalEnd + (isRight ? 8 : -8), p.targetY + 13);
            } else {
                ctx.font = `lighter ${13 * (window.devicePixelRatio || 1)}px 'Segoe UI', sans-serif`;
                ctx.fillText(p.anomaly_type, horizontalEnd + (isRight ? 8 : -8), p.targetY + 4);
            }
        });
    }

    function drawCenterText(centerX, centerY, timestamp) {
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `200 ${20 * (window.devicePixelRatio || 1)}px 'Segoe UI', sans-serif`;
        ctx.letterSpacing = "6px";
        ctx.fillStyle = 'rgba(161, 227, 64, 1)';
        ctx.fillText(timestamp, centerX, centerY - 15);

        ctx.font = `bold ${13 * (window.devicePixelRatio || 1)}px 'Segoe UI', sans-serif`;
        ctx.fillStyle = 'rgba(241, 245, 235, 0.9)';
        ctx.fillText('TRAFFIX ENGINE', centerX, centerY + 25);
        ctx.restore();
    }

    let animationId;

    function draw() {
        ctx.fillStyle = '#030603';
        ctx.fillRect(0, 0, width, height);

        const centerX = width / 2;
        const centerY = height / 2;
        globalTime += config.globalSpeed;

        const kf_start = Math.floor(dataFrame / framesToInterpolate) % frames.length;
        const kf_end = (kf_start + 1) % frames.length;
        const alpha = (dataFrame % framesToInterpolate) / framesToInterpolate;

        const rStart = frames[kf_start].radii;
        const rEnd = frames[kf_end].radii;
        const anomaliesStart = frames[kf_start].anomalies;
        const anomaliesEnd = frames[kf_end].anomalies;
        const currentTimestamp = frames[kf_start].timestamp.substring(11, 16);

        let currentMaxR = 0;
        for (let i = 0; i < thetas.length; i++) {
            const r = rStart[i] * (1 - alpha) + rEnd[i] * alpha;
            if (r > currentMaxR) currentMaxR = r;
        }
        const currentMaxHeight = getPeakHeight(currentMaxR);
        const dynamicGlow = 10 + (currentMaxHeight * 0.4);

        for (let l = 0; l < config.layers; l++) {
            const layerProgress = l / config.layers;
            const opacity = 0.05 + (0.18 * layerProgress);
            ctx.beginPath();
            ctx.lineWidth = 0.6 + (layerProgress * 0.4);
            ctx.strokeStyle = `rgba(161, 227, 64, ${opacity})`;

            for (let i = 0; i <= thetas.length; i++) {
                const idx = i % thetas.length;
                const angle = thetas[idx];
                const r = getRadius(idx, l, layerProgress, rStart, rEnd, alpha);

                const x = centerX + Math.cos(angle) * r;
                const y = centerY + Math.sin(angle) * r;
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            }
            ctx.stroke();
        }

        ctx.beginPath();
        ctx.lineWidth = 2.4;
        ctx.strokeStyle = config.baseColor;

        ctx.shadowBlur = dynamicGlow;
        ctx.shadowColor = `rgba(161, 227, 64, ${0.3 + (currentMaxHeight / 200)})`;

        for (let i = 0; i <= thetas.length; i++) {
            const idx = i % thetas.length;
            const angle = thetas[idx];
            const r = getRadius(idx, -1, 1.0, rStart, rEnd, alpha);

            const x = centerX + Math.cos(angle) * r;
            const y = centerY + Math.sin(angle) * r;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.stroke();
        ctx.shadowBlur = 0;

        drawCenterText(centerX, centerY, currentTimestamp);
        drawLabels(centerX, centerY, rStart, rEnd, alpha, anomaliesStart, anomaliesEnd);

        dataFrame++;
        animationId = requestAnimationFrame(draw);
    }

    window.addEventListener('resize', resize);
    resize();
    animationId = requestAnimationFrame(draw);

    return () => {
        window.removeEventListener('resize', resize);
        cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <div className={styles.loadingContainer}>
      <canvas ref={canvasRef} className={styles.vizCanvas} />
    </div>
  );
};

export default TraffixLoadingScreen;
